import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useScope } from "@/hooks/use-scope";
import { launchKitFor, blueprintLabel, type BlueprintKey, type LaunchKitItem } from "@/lib/launch-kit";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Download,
  FileText,
  FileSpreadsheet,
  PlayCircle,
  CheckCircle2,
  Circle,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/implementation/")({
  component: WelcomePage,
  head: () => ({
    meta: [
      { title: "Welcome — CairnOS" },
      { name: "description", content: "Begin implementing your warehouse on CairnOS." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function greetingForNow(): string {
  const h = new Date().getHours();
  if (h < 5) return "Good evening";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function firstNameOf(fullName?: string | null, email?: string | null): string {
  if (fullName && fullName.trim()) return fullName.trim().split(/\s+/)[0];
  if (email) return email.split("@")[0].split(/[._-]/)[0].replace(/^./, (c) => c.toUpperCase());
  return "there";
}

function todaysObjective(hasFacility: boolean, hasBlueprint: boolean): string {
  if (!hasBlueprint) return "Select your Blueprint and build your warehouse foundation.";
  if (!hasFacility) return "Configure your first facility.";
  return "Continue building your warehouse foundation.";
}

function WelcomePage() {
  const { user } = useAuth();
  const { tenantId, tenants, facilities } = useScope();

  const tenant = tenants.find((t) => t.id === tenantId);
  const tenantName = tenant?.name ?? "your workspace";

  // Blueprint lives on the tenants row; scope doesn't select it, so read it here.
  const { data: blueprintRow } = useQuery({
    queryKey: ["tenant-blueprint", tenantId],
    enabled: !!tenantId,
    queryFn: async () =>
      (await supabase.from("tenants").select("blueprint").eq("id", tenantId!).maybeSingle()).data,
  });
  const blueprint = (blueprintRow?.blueprint ?? null) as BlueprintKey | null;

  const facilityCount = facilities.filter((f) => f.tenant_id === tenantId).length;
  const hasFacility = facilityCount > 0;
  const hasBlueprint = !!blueprint;

  const kit = useMemo(() => launchKitFor(blueprint), [blueprint]);
  const firstName = firstNameOf(
    (user?.user_metadata as { full_name?: string } | undefined)?.full_name,
    user?.email,
  );

  return (
    <div className="min-h-[calc(100vh-var(--app-shell-header,3.5rem))] bg-background">
      {/* Ambient background — subtle gradient behind the hero */}
      <div className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.55]"
          style={{
            background:
              "radial-gradient(ellipse 60% 45% at 20% 0%, color-mix(in oklab, var(--primary) 18%, transparent), transparent 60%), radial-gradient(ellipse 50% 40% at 90% 10%, color-mix(in oklab, var(--chart-2) 14%, transparent), transparent 60%)",
          }}
        />

        <div className="relative mx-auto max-w-5xl px-6 pt-16 pb-14 sm:pt-24 sm:pb-20">
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-700">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
              CairnOS · Implementation
            </div>

            <h1 className="mt-6 text-4xl font-semibold tracking-tight text-foreground sm:text-6xl">
              {greetingForNow()}, {firstName}.
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
              You're about to implement <span className="text-foreground font-medium">{tenantName}</span>'s
              warehouse on CairnOS. Your Blueprint will determine how CairnOS recommends policies,
              integrations, and operational behavior — everything else is yours to shape.
            </p>

            {/* Live stat strip */}
            <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-3">
              <StatCell
                label="Estimated time"
                value="2 – 5 days"
                hint="Foundation, Data, Operations, Validation"
              />
              <StatCell
                label="Blueprint"
                value={blueprintLabel(blueprint)}
                hint={hasBlueprint ? "Recommendations ready" : "Selected during Foundation"}
                tone={hasBlueprint ? "default" : "pending"}
              />
              <StatCell
                label="Today's objective"
                value={todaysObjective(hasFacility, hasBlueprint)}
                emphasis
              />
            </div>

            {/* Primary CTA */}
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="h-12 px-6 text-base shadow-sm">
                <Link to="/app/implementation/workspace">
                  Begin Implementation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <a
                href="#launch-kit"
                className="inline-flex h-12 items-center rounded-md px-4 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Or start with the Launch Kit
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Video / walkthrough placeholder */}
      <section className="mx-auto max-w-5xl px-6">
        <div className="animate-in fade-in duration-1000">
          <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-card)]">
            <div
              aria-hidden
              className="absolute inset-0 opacity-40"
              style={{
                background:
                  "linear-gradient(135deg, color-mix(in oklab, var(--primary) 10%, transparent) 0%, transparent 45%, color-mix(in oklab, var(--chart-2) 10%, transparent) 100%)",
              }}
            />
            <div className="relative flex flex-col items-start gap-5 p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10">
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                  <PlayCircle className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                    Welcome walkthrough
                  </div>
                  <div className="mt-1 text-lg font-medium text-foreground">
                    A five-minute tour of the four tracks.
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    Coming soon — filmed with your Implementation Partner.
                  </div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Video placeholder
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Launch Kit */}
      <section id="launch-kit" className="mx-auto max-w-5xl px-6 py-16 sm:py-24">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
              Launch Kit
            </div>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Everything you need to start today.
            </h2>
            <p className="mt-3 max-w-2xl text-base text-muted-foreground">
              The Launch Kit is your working set for the first days of implementation.
              Download it, share it with your team, and begin populating your data.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--success)]" />
            Ready to download
          </div>
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {kit.map((item) => (
            <KitCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* What's ahead — the four tracks preview */}
      <section className="border-t border-border bg-[color:var(--secondary)]">
        <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
            What's ahead
          </div>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            Four tracks, running in parallel.
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Implementation on CairnOS is not a wizard. It's four tracks with clear owners,
            clear evidence, and one shared definition of done.
          </p>

          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <TrackRow
              index="01"
              name="Foundation"
              promise="The infrastructure your warehouse runs on."
              status="ready"
            />
            <TrackRow
              index="02"
              name="Data"
              promise="The state your warehouse's promises will defend."
              status="upcoming"
            />
            <TrackRow
              index="03"
              name="Operations"
              promise="The ratified decisions that govern automated behavior."
              status="upcoming"
            />
            <TrackRow
              index="04"
              name="Validation"
              promise="Proof — not configuration."
              status="upcoming"
            />
          </div>

          <div className="mt-10">
            <Button asChild size="lg" variant="default" className="h-11 px-5">
              <Link to="/app/implementation/workspace">
                Enter the Workspace
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCell({
  label,
  value,
  hint,
  emphasis,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  emphasis?: boolean;
  tone?: "default" | "pending";
}) {
  return (
    <div className="bg-card p-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div
        className={
          emphasis
            ? "mt-2 text-base font-medium text-foreground leading-snug"
            : "mt-2 text-lg font-semibold text-foreground"
        }
      >
        {value}
      </div>
      {hint ? (
        <div
          className={
            "mt-1 text-xs " +
            (tone === "pending" ? "text-[color:var(--warning)]" : "text-muted-foreground")
          }
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function KitCard({ item }: { item: LaunchKitItem }) {
  const Icon = item.kind === "guide" ? FileText : FileSpreadsheet;
  return (
    <a
      href={item.href}
      download={item.filename}
      className="group relative flex items-start gap-4 rounded-xl border border-border bg-card p-5 transition-all hover:border-primary/40 hover:shadow-[var(--shadow-card)]"
    >
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/8 text-primary transition-colors group-hover:bg-primary/12">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-medium text-foreground">{item.title}</div>
          <Download className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
        </div>
        <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {item.description}
        </div>
        <div className="mt-2 font-mono text-[11px] text-muted-foreground/80">
          {item.filename}
        </div>
      </div>
    </a>
  );
}

function TrackRow({
  index,
  name,
  promise,
  status,
}: {
  index: string;
  name: string;
  promise: string;
  status: "ready" | "upcoming";
}) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-border bg-card p-5">
      <div className="font-mono text-xs text-muted-foreground/70">{index}</div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-foreground">{name}</div>
          {status === "ready" ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--success)]/20 bg-[color:var(--success)]/10 px-2 py-0.5 text-[10px] font-medium text-[color:var(--success)]">
              <CheckCircle2 className="h-3 w-3" />
              Available
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              <Circle className="h-3 w-3" />
              Coming
            </span>
          )}
        </div>
        <div className="mt-1 text-xs leading-relaxed text-muted-foreground">{promise}</div>
      </div>
    </div>
  );
}